/**
 * Global Config Service
 *
 * Centralized singleton that loads the globalConfig/default document once
 * and caches it in memory. Avoids repeated Firestore reads across the app.
 *
 * Usage:
 *   await globalConfigService.load();            // at bootstrap / login
 *   const cfg = globalConfigService.getConfig();  // synchronous read
 */
import { repositoryResolver } from '@rammetec/data-access';
import { rtLogService } from '@rammetec/core';
import { SYSTEM_API_INTEGRATION_FIELDS, buildSystemApiIntegration, isSystemApiIntegrationField, } from './systemApiIntegrations.js';
class GlobalConfigService {
    constructor() {
        this.cachedConfig = null;
    }
    /**
     * Fetches globalConfig/default from Firestore and caches it in memory.
     * When isDev is true, also loads globalConfig/dev and merges its fields
     * over the default config (dev overrides only the fields it contains,
     * e.g. localhost URLs instead of production endpoints).
     *
     * @param isDev - Whether the app is running in development mode
     */
    async load(isDev = false) {
        try {
            const repo = await repositoryResolver.resolve('GlobalConfig');
            const doc = await repo.getById('default');
            this.cachedConfig = doc;
            // In dev mode, load the "dev" document and merge its fields over the default
            if (isDev && doc) {
                try {
                    const devDoc = await repo.getById('dev');
                    if (devDoc) {
                        // Merge only the fields present in devDoc (skip id)
                        for (const key of Object.keys(devDoc)) {
                            if (key === 'id')
                                continue;
                            if (devDoc[key] !== undefined && devDoc[key] !== null) {
                                this.cachedConfig[key] = devDoc[key];
                            }
                        }
                        rtLogService.info('[GlobalConfigService] Dev overrides applied', {
                            overriddenKeys: Object.keys(devDoc).filter(k => k !== 'id'),
                        });
                    }
                }
                catch {
                    // dev doc may not exist, that's fine
                    rtLogService.info('[GlobalConfigService] No dev config found, using defaults only');
                }
            }
            rtLogService.info('[GlobalConfigService] Config loaded', {
                isDev,
                hasMonitorHost: !!this.cachedConfig?.monitorHost,
                hasSystemApiHost: !!this.cachedConfig?.systemApiHost,
                hasDefaultPlanIds: !!(this.cachedConfig?.defaultPlanIds &&
                    this.cachedConfig.defaultPlanIds.length > 0),
            });
        }
        catch (error) {
            rtLogService.warn('[GlobalConfigService] Unable to load globalConfig/default', { error });
        }
    }
    /**
     * Returns the cached GlobalConfig (synchronous).
     * Returns null if load() has not been called or failed.
     */
    getConfig() {
        return this.cachedConfig;
    }
    /**
     * Injects the env-level override for the System API HTTP host (highest
     * precedence). Called by the console bootstrap, which owns env access.
     * Pass `undefined`/empty to clear the override.
     */
    setSystemApiHostOverride(host) {
        this.systemApiHostOverride = host?.trim() || undefined;
    }
    /**
     * Resolves the System API HTTP host using the precedence:
     * env override → globalConfig (dev merged over default). Returns '' when none.
     */
    resolveSystemApiHost() {
        if (this.systemApiHostOverride)
            return this.systemApiHostOverride;
        const fromConfig = this.cachedConfig?.systemApiHost;
        return typeof fromConfig === 'string' ? fromConfig.trim() : '';
    }
    /**
     * Returns an integration config for the given GlobalConfig field.
     *
     * For the known System API integrations, the config is BUILT FROM SOURCE using
     * the configured `systemApiHost` address — the full object is no longer stored
     * in GlobalConfig (see {@link SYSTEM_API_INTEGRATION_FIELDS}).
     *
     * For any other field, the value is read from the cached document (object or
     * legacy JSON string), preserving the previous behaviour.
     *
     * @param fieldName - The GlobalConfig field name identifying the integration
     */
    getIntegrationConfig(fieldName) {
        if (!this.cachedConfig)
            return null;
        // Known System API integrations are derived from the resolved HTTP host
        // (env override → globalConfig/dev → globalConfig/default).
        if (typeof fieldName === 'string' && isSystemApiIntegrationField(fieldName)) {
            const host = this.resolveSystemApiHost();
            if (!host) {
                rtLogService.warn('[GlobalConfigService] systemApiHost not configured — cannot build integration', { fieldName });
                return null;
            }
            return buildSystemApiIntegration(host, SYSTEM_API_INTEGRATION_FIELDS[fieldName]);
        }
        const value = this.cachedConfig[fieldName];
        if (!value)
            return null;
        // Firestore may store the value as a JSON string — parse it if needed
        if (typeof value === 'string') {
            try {
                return JSON.parse(value);
            }
            catch {
                rtLogService.warn('[GlobalConfigService] Failed to parse integration config as JSON', { fieldName });
                return null;
            }
        }
        if (typeof value === 'object') {
            return value;
        }
        return null;
    }
    /**
     * Forces a re-fetch from Firestore. Alias for load().
     */
    async reload() {
        return this.load();
    }
    /**
     * Clears the in-memory cache.
     */
    clear() {
        this.cachedConfig = null;
    }
}
export const globalConfigService = new GlobalConfigService();
//# sourceMappingURL=globalConfigService.js.map